<!-- eslint-disable vue/no-bare-strings-in-template, @intlify/vue-i18n/no-raw-text -->
<script setup>
import { computed, ref } from 'vue';
import { useMessageContext } from '../provider.js';
import Icon from 'dashboard/components-next/icon/Icon.vue';
import { useAlert } from 'dashboard/composables';
import { useMapGetter } from 'dashboard/composables/store';

const {
  content,
  contentAttributes,
  id: messageId,
  conversationId,
  sender,
} = useMessageContext();
const accountId = useMapGetter('getCurrentAccountId');
const currentChat = useMapGetter('getSelectedChat');
const attrs = computed(() => contentAttributes.value || {});
const videoId = computed(
  () => attrs.value.tiktok_video_id || attrs.value.tiktokVideoId
);
const permalink = computed(() => attrs.value.permalink);
const caption = computed(
  () => attrs.value.caption || 'Vídeo publicado no TikTok'
);
const author = computed(
  () => sender.value?.name || attrs.value.author_name || 'Pessoa no TikTok'
);
const imageUrl = computed(
  () =>
    attrs.value.image_url ||
    attrs.value.cover_image_url ||
    (videoId.value?.startsWith('local-')
      ? '/packs/dashboard/images/onboarding/omnichannel-inbox.png'
      : null)
);
const hidden = ref(null);
const liked = ref(null);
const deleted = ref(false);
const replyText = ref('');
const sending = ref(false);
const labels = {
  context: 'Contexto do vídeo no TikTok',
  liked: 'Curtido',
  like: 'Curtir',
  show: 'Exibir',
  hide: 'Ocultar',
  delete: 'Excluir',
  placeholder: 'Responder publicamente...',
  publish: 'Publicar',
  video: 'Vídeo',
  publication: 'Publicação',
  deletedMessage: 'Comentário excluído no TikTok',
  hiddenMessage: 'Comentário oculto no TikTok',
  commentBy: 'Comentário de',
};
const openPost = () => {
  if (permalink.value)
    window.open(permalink.value, '_blank', 'noopener,noreferrer');
};
const replyClass = depth =>
  ['ml-0', 'ml-3', 'ml-6', 'ml-9', 'ml-12'][Math.min(depth || 0, 4)];

const isHidden = computed(() => hidden.value ?? attrs.value.is_hidden ?? false);
const isLiked = computed(
  () => liked.value ?? attrs.value.is_liked_by_business ?? false
);
const rootId = computed(
  () => attrs.value.tiktok_comment_id || attrs.value.tiktokCommentId
);
const replies = computed(() => {
  const messages = currentChat.value?.messages || [];
  const all = messages.filter(
    message => message.content_attributes?.tiktok_reply_type === 'public'
  );
  const children = all.reduce((map, message) => {
    const parent = message.content_attributes?.in_reply_to_external_id;
    if (parent) (map[parent] ||= []).push(message);
    return map;
  }, {});
  const result = [];
  const append = (parent, depth) =>
    (children[parent] || []).forEach(reply => {
      result.push({ ...reply, depth });
      append(reply.source_id, depth + 1);
    });
  append(rootId.value, 0);
  return result;
});

const action = async (name, targetId = messageId.value) => {
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages/${targetId}/tiktok_actions`,
      { tt_action: name }
    );
    if (targetId === messageId.value) {
      if (name === 'like' || name === 'unlike') liked.value = name === 'like';
      if (name === 'hide' || name === 'unhide') hidden.value = name === 'hide';
      if (name === 'delete') deleted.value = true;
    }
    useAlert('Ação confirmada no TikTok.');
  } catch (error) {
    useAlert(error.response?.data?.error || 'O TikTok recusou esta ação.');
  }
};

const sendReply = async () => {
  if (!replyText.value.trim() || sending.value) return;
  sending.value = true;
  try {
    await window.axios.post(
      `/api/v1/accounts/${accountId.value}/conversations/${conversationId.value}/messages`,
      {
        content: replyText.value.trim(),
        content_attributes: {
          tiktok_reply_type: 'public',
          in_reply_to_external_id: rootId.value,
          is_tiktok_post_context: true,
          tiktok_video_id: videoId.value,
          permalink: permalink.value,
          caption: caption.value,
          image_url: imageUrl.value,
        },
      }
    );
    replyText.value = '';
    useAlert('Resposta publicada no TikTok.');
  } catch (error) {
    useAlert(
      error.response?.data?.error || 'Não foi possível responder no TikTok.'
    );
  } finally {
    sending.value = false;
  }
};
</script>

<template>
  <div class="flex max-w-sm flex-col gap-2">
    <div
      class="flex flex-col overflow-hidden rounded-xl border border-slate-200 bg-white shadow-sm transition-shadow hover:shadow-md dark:border-slate-700 dark:bg-slate-800"
    >
      <div
        class="flex items-center border-b border-slate-100 bg-slate-50 px-3 py-2 dark:border-slate-800 dark:bg-slate-900"
      >
        <Icon
          icon="brand-tiktok"
          size="16"
          class="mr-2 text-slate-900 dark:text-white"
        />
        <button
          type="button"
          class="text-xs font-medium text-slate-700 underline-offset-2 hover:underline dark:text-slate-300"
          :disabled="!permalink"
          @click="openPost"
        >
          {{ labels.context }}
        </button>
        <span
          class="ml-auto text-[11px] font-semibold uppercase tracking-wide text-slate-400"
        >
          {{ labels.video }}
        </span>
      </div>

      <div
        v-if="imageUrl"
        class="relative h-48 w-full overflow-hidden bg-slate-100 dark:bg-slate-900"
      >
        <img
          :src="imageUrl"
          class="h-full w-full object-cover"
          alt="Vídeo do TikTok"
          loading="lazy"
        />
      </div>

      <div class="p-3">
        <p
          class="mb-1 text-[11px] font-semibold uppercase tracking-wide text-slate-500 dark:text-slate-400"
        >
          {{ labels.publication }}
        </p>
        <p class="line-clamp-3 text-sm text-slate-700 dark:text-slate-300">
          {{ caption }}
        </p>
      </div>

      <div
        class="border-t border-slate-100 bg-indigo-50 p-3 dark:border-slate-800 dark:bg-indigo-900/20"
      >
        <div
          v-if="deleted"
          class="mb-2 flex items-center gap-2 rounded bg-red-100/50 p-2 dark:bg-red-900/30"
        >
          <Icon icon="i-lucide-trash-2" class="text-red-600" size="14" />
          <span class="text-sm font-medium italic text-red-600">{{
            labels.deletedMessage
          }}</span>
        </div>
        <div
          v-else-if="isHidden"
          class="mb-2 flex items-center gap-2 rounded bg-slate-200 p-2 dark:bg-slate-800"
        >
          <Icon icon="i-lucide-eye-off" class="text-slate-500" size="14" />
          <span class="text-sm font-medium italic text-slate-500">{{
            labels.hiddenMessage
          }}</span>
        </div>

        <div class="flex items-start gap-2.5">
          <div
            class="mt-0.5 flex size-8 shrink-0 items-center justify-center rounded-full bg-slate-200 text-[11px] font-bold text-slate-700 dark:bg-slate-700 dark:text-slate-200"
            aria-hidden="true"
          >
            {{
              author
                .split(/\s+/)
                .filter(Boolean)
                .slice(0, 2)
                .map(part => part[0]?.toUpperCase())
                .join('') || 'TT'
            }}
          </div>
          <div class="min-w-0 flex-1">
            <p
              class="mb-1 text-[11px] font-semibold text-slate-500 dark:text-slate-400"
            >
              {{ labels.commentBy }} {{ author }}
            </p>
            <p
              class="whitespace-pre-wrap text-sm font-medium transition-all"
              :class="
                deleted || isHidden
                  ? 'italic text-slate-400 line-through dark:text-slate-500'
                  : 'text-slate-800 dark:text-slate-200'
              "
            >
              {{ content }}
            </p>
          </div>
        </div>

        <div
          v-if="!deleted"
          class="mt-3 flex items-center justify-between border-t border-indigo-100 pt-2 dark:border-indigo-800/30"
        >
          <button
            type="button"
            class="flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium transition-colors hover:bg-slate-100 disabled:cursor-not-allowed disabled:opacity-40 dark:hover:bg-slate-800/50"
            :class="
              isLiked ? 'text-slate-800 dark:text-slate-200' : 'text-slate-500'
            "
            :disabled="isHidden"
            :aria-pressed="isLiked"
            @click="action(isLiked ? 'unlike' : 'like')"
          >
            <Icon
              :icon="isLiked ? 'i-lucide-heart' : 'i-lucide-heart'"
              size="14"
            />
            <span>{{ isLiked ? labels.liked : labels.like }}</span>
          </button>
          <button
            type="button"
            class="flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-slate-100 hover:text-slate-800 dark:hover:bg-slate-800/50 dark:hover:text-slate-300"
            @click="action(isHidden ? 'unhide' : 'hide')"
          >
            <Icon
              :icon="isHidden ? 'i-lucide-eye' : 'i-lucide-eye-off'"
              size="14"
            />
            <span>{{ isHidden ? labels.show : labels.hide }}</span>
          </button>
          <button
            type="button"
            class="flex flex-1 items-center justify-center gap-1.5 rounded-md py-1.5 text-xs font-medium text-slate-500 transition-colors hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-900/20"
            @click="action('delete')"
          >
            <Icon icon="i-lucide-trash-2" size="14" />
            <span>{{ labels.delete }}</span>
          </button>
        </div>
      </div>

      <div
        v-if="replies.length"
        class="flex flex-col border-t border-slate-200 bg-slate-50 dark:border-slate-700 dark:bg-slate-900/50"
      >
        <div
          v-for="reply in replies"
          :key="reply.id"
          class="flex gap-2 border-b border-slate-100 p-3 last:border-0 dark:border-slate-800"
          :class="replyClass(reply.depth)"
        >
          <div
            class="flex size-7 shrink-0 items-center justify-center rounded-full bg-purple-100 text-[11px] font-bold text-purple-600 dark:bg-purple-900/30"
          >
            {{ (reply.sender?.name || 'Você').charAt(0).toUpperCase() }}
          </div>
          <div class="min-w-0">
            <p
              class="text-[11px] font-semibold text-slate-500 dark:text-slate-400"
            >
              {{ reply.sender?.name || 'Você' }}
            </p>
            <p
              class="whitespace-pre-wrap text-sm text-slate-800 dark:text-slate-200"
            >
              {{ reply.content }}
            </p>
          </div>
        </div>
      </div>

      <div
        class="flex gap-2 border-t border-slate-200 p-3 dark:border-slate-700"
      >
        <input
          v-model="replyText"
          class="min-w-0 flex-1 rounded border border-slate-300 bg-white px-2 py-2 text-sm dark:border-slate-600 dark:bg-slate-900"
          :placeholder="labels.placeholder"
          :disabled="isHidden || deleted"
          @keydown.enter.prevent="sendReply"
        />
        <button
          type="button"
          class="text-sm font-medium text-sky-600 disabled:opacity-40"
          :disabled="sending || isHidden || deleted"
          @click="sendReply"
        >
          {{ labels.publish }}
        </button>
      </div>
    </div>
  </div>
</template>
